import type { TFunction } from '@mukil/i18n';
import { FormField, Input, StyledSelect } from '@jumpstone/ui-react';
import { EmployeeFormLkTaxFields } from './employee_form_lk_tax_fields';
import {
  BPJS_JKK_RISK_CLASSES,
  PTKP_STATUS_OPTIONS,
  type EmployeeFormDraft,
  type EmployeeFormErrors,
  type EmployeeFormFieldError,
  type EmployeeJurisdiction,
} from './employee_form_validation';

const SELECT_TRIGGER_CLASSES =
  'nv-input flex h-9 w-full cursor-pointer items-center justify-between gap-2';

const FIELD_ERROR_KEY: Record<EmployeeFormFieldError, string> = {
  required: 'employees.form_error_required',
  too_long: 'employees.form_error_too_long',
  invalid_email: 'employees.form_error_invalid_email',
  salary_required: 'employees.form_error_salary_required',
  negative_amount: 'employees.form_error_negative_amount',
  termination_before_start: 'employees.form_error_termination_before_start',
  invalid_nic: 'employees.form_error_invalid_nic',
  invalid_nic_day: 'employees.form_error_invalid_nic_day',
  epf_empty: 'employees.form_error_epf_empty',
  epf_control_chars: 'employees.form_error_epf_control_chars',
  invalid_risk_class: 'employees.form_error_invalid_risk_class',
  fte_required: 'employees.form_error_fte_required',
  fte_out_of_range: 'employees.form_error_fte_out_of_range',
  effective_from_before_start:
    'employees.form_error_effective_from_before_start',
  history_invalid: 'employees.form_error_history_invalid',
};

export interface EmployeeFormJurisdictionFieldsProps {
  draft: EmployeeFormDraft;
  errors: EmployeeFormErrors;
  jurisdiction: EmployeeJurisdiction;
  t: TFunction;
  set: <K extends keyof EmployeeFormDraft>(
    key: K,
    value: EmployeeFormDraft[K],
  ) => void;
}

/**
 * The two jurisdiction-specific ID field groups (Sri Lanka NIC/EPF, Indonesia
 * NPWP/NIK/PTKP/BPJS) -- split out of `employee_form_fields.tsx` (CLAUDE.md's
 * "Components >150 lines" rule) purely to keep each file's line count down;
 * this is still pure presentation, no state of its own. Renders nothing for
 * any other jurisdiction -- see `employee_form_validation.ts`'s
 * `deriveJurisdiction` for how the jurisdiction itself is derived.
 */
export function EmployeeFormJurisdictionFields({
  draft,
  errors,
  jurisdiction,
  t,
  set,
}: EmployeeFormJurisdictionFieldsProps): React.JSX.Element | null {
  // A contractor is not an employee: no EPF membership (LK), PTKP status or
  // BPJS JKK risk class (ID) applies. Tax IDs (NIC / NPWP / NIK) stay --
  // they identify the worker and are needed when paying them by bill.
  const is_contractor = draft.employment_type === 'contractor';

  if (jurisdiction === 'LK') {
    return (
      <>
        <FormField
          label={t('employees.form_nic_number')}
          htmlFor="employee-form-nic-number"
          error={errors.nic_number && t(FIELD_ERROR_KEY[errors.nic_number])}
        >
          <Input
            id="employee-form-nic-number"
            value={draft.nic_number}
            onChange={(e) => {
              set('nic_number', e.target.value);
            }}
          />
        </FormField>

        {!is_contractor && (
          <FormField
            label={t('employees.form_epf_member_no')}
            htmlFor="employee-form-epf-member-no"
            error={
              errors.epf_member_no && t(FIELD_ERROR_KEY[errors.epf_member_no])
            }
          >
            <Input
              id="employee-form-epf-member-no"
              value={draft.epf_member_no}
              onChange={(e) => {
                set('epf_member_no', e.target.value);
              }}
            />
          </FormField>
        )}

        {!is_contractor && (
          <EmployeeFormLkTaxFields
            draft={draft}
            errors={errors}
            t={t}
            set={set}
          />
        )}
      </>
    );
  }

  if (jurisdiction === 'ID') {
    return (
      <>
        <FormField
          label={t('employees.form_npwp')}
          htmlFor="employee-form-npwp"
          error={errors.npwp && t(FIELD_ERROR_KEY[errors.npwp])}
        >
          <Input
            id="employee-form-npwp"
            value={draft.npwp}
            onChange={(e) => {
              set('npwp', e.target.value);
            }}
          />
        </FormField>

        <FormField
          label={t('employees.form_nik')}
          htmlFor="employee-form-nik"
          error={errors.nik && t(FIELD_ERROR_KEY[errors.nik])}
        >
          <Input
            id="employee-form-nik"
            value={draft.nik}
            onChange={(e) => {
              set('nik', e.target.value);
            }}
          />
        </FormField>

        {!is_contractor && (
          <FormField label={t('employees.form_ptkp_status')}>
            <StyledSelect
              value={draft.ptkp_status}
              onChange={(value) => {
                set('ptkp_status', value);
              }}
              options={[
                { value: '', label: t('employees.form_ptkp_unset') },
                ...PTKP_STATUS_OPTIONS.map((status) => ({
                  value: status,
                  label: status,
                })),
              ]}
              ariaLabel={t('employees.form_ptkp_status')}
              triggerClassName={SELECT_TRIGGER_CLASSES}
            />
          </FormField>
        )}

        {!is_contractor && (
          <FormField
            label={t('employees.form_bpjs_jkk_risk_class')}
            error={
              errors.bpjs_jkk_risk_class &&
              t(FIELD_ERROR_KEY[errors.bpjs_jkk_risk_class])
            }
          >
            <StyledSelect
              value={String(draft.bpjs_jkk_risk_class ?? '')}
              onChange={(value) => {
                set('bpjs_jkk_risk_class', value === '' ? null : Number(value));
              }}
              options={[
                { value: '', label: t('employees.form_ptkp_unset') },
                ...BPJS_JKK_RISK_CLASSES.map((riskClass) => ({
                  value: String(riskClass),
                  label: t('employees.form_bpjs_jkk_risk_class_option', {
                    risk_class: riskClass,
                  }),
                })),
              ]}
              ariaLabel={t('employees.form_bpjs_jkk_risk_class')}
              triggerClassName={SELECT_TRIGGER_CLASSES}
            />
          </FormField>
        )}
      </>
    );
  }

  return null;
}
